import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { friendsAPI, chatAPI } from '../services/api';
import { io, Socket } from 'socket.io-client';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [pendingRequests, setPendingRequests] = useState<any[]>([]);
  const [unreadChatCount, setUnreadChatCount] = useState<number>(0);
  const [socket, setSocket] = useState<Socket | null>(null);

    useEffect(() => {
    if (user) {
      loadPendingRequests();
      loadUnreadChatCount();
      
      // Initialize socket connection for notifications
      const token = localStorage.getItem('token');
      if (token) {
        const newSocket = io('/', {
          auth: { token },
        });

        newSocket.on('connect', () => {
          console.log('Navbar: Connected to socket');
          newSocket.emit('joinUserRoom', { userId: user.id });
        });

        newSocket.on('friendRequestReceived', (data) => {
          console.log('Friend request received in navbar:', data);
          loadPendingRequests(); // Refresh pending requests
          
          // Show notification toast
          const notification = document.createElement('div');
          notification.className = 'notification-toast';
          notification.innerHTML = `
            <div style="
              position: fixed;
              top: 20px;
              right: 20px;
              background: rgba(0, 123, 255, 0.1);
              border: 1px solid rgba(0, 123, 255, 0.3);
              border-radius: 8px;
              padding: 12px 16px;
              color: #007bff;
              backdrop-filter: blur(10px);
              z-index: 1001;
              min-width: 300px;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
              animation: slideIn 0.3s ease-out;
            ">
              <div style="display: flex; align-items: center; justify-content: space-between;">
                <div style="display: flex; align-items: center;">
                  <span style="margin-right: 8px; font-size: 16px;">ℹ</span>
                  <span>New friend request from ${data.from.firstName} ${data.from.lastName}</span>
                </div>
                <button onclick="this.parentElement.parentElement.remove()" style="
                  background: none;
                  border: none;
                  color: #007bff;
                  cursor: pointer;
                  font-size: 18px;
                  margin-left: 12px;
                  padding: 0;
                  width: 20px;
                  height: 20px;
                  display: flex;
                  align-items: center;
              justify-content: center;
                ">×</button>
              </div>
            </div>
          `;
          document.body.appendChild(notification);
          
          // Auto-remove after 5 seconds
          setTimeout(() => {
            if (notification.parentElement) {
              notification.remove();
            }
          }, 5000);
        });

        newSocket.on('newMessage', (data) => {
          console.log('New message received in navbar:', data);
          loadUnreadChatCount(); // Refresh unread count
        });

        // Listen for conversation updates (when messages are marked as read)
        newSocket.on('conversationUpdated', (data) => {
          console.log('Conversation updated in navbar:', data);
          loadUnreadChatCount(); // Refresh unread count
        });

        setSocket(newSocket);

        return () => {
          newSocket.close();
        };
      }
    }
  }, [user]);

  const loadPendingRequests = async () => {
    try {
      const response = await friendsAPI.getPendingRequests();
      const receivedRequests = response.data.filter((r: any) => !r.isSent);
      setPendingRequests(receivedRequests);
    } catch (error) {
      console.error('Error loading pending requests:', error);
    }
  };

  const loadUnreadChatCount = async () => {
    try {
      const response = await chatAPI.getUnreadCount();
      setUnreadChatCount(response.data.conversationsWithUnread);
    } catch (error) {
      console.error('Error loading unread chat count:', error);
    }
  };



  return (
    <nav className="navbar navbar-expand-lg navbar-dark">
      <div className="container-fluid">
        <Link className="navbar-brand" to="/dashboard">
          <img src="/logo.png" alt="Tellcrypt" style={{ width: '30px', height: '30px', marginRight: '8px' }} />
          Tellcrypt
        </Link>
        {user && (
          <>
            <button 
              className="navbar-toggler" 
              type="button" 
              data-bs-toggle="collapse" 
              data-bs-target="#mainNav" 
              aria-controls="mainNav" 
              aria-expanded="false" 
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon"></span>
            </button>
            <div className="collapse navbar-collapse" id="mainNav">
              <ul className="navbar-nav me-auto">
                <li className="nav-item">
                  <Link className="nav-link" to="/dashboard">{t('navbar.dashboard')}</Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/friends">{t('navbar.friends')}</Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link position-relative" to="/chat">
                    {t('navbar.chat')}
                    {unreadChatCount > 0 && (
                      <span 
                        className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                        style={{ fontSize: '0.6rem', transform: 'translate(-50%, -50%)' }}
                      >
                        {unreadChatCount}
                      </span>
                    )}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link position-relative" to="/invitations">
                    {t('navbar.invitations')}
                    {pendingRequests.length > 0 && (
                      <span 
                        className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                        style={{ fontSize: '0.6rem', transform: 'translate(-50%, -50%)' }}
                      >
                        {pendingRequests.length}
                      </span>
                    )}
                  </Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/history">{t('navbar.history')}</Link>
                </li>
                <li className="nav-item">
                  <Link className="nav-link" to="/profile">{t('navbar.profile')}</Link>
                </li>
                <li className="nav-item">
                  <button 
                    className="nav-link" 
                    onClick={logout}
                    style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.7)' }}
                  >
                    {t('navbar.logout')}
                  </button>
                </li>
              </ul>
              
              {/* Language Selector */}
              <div className="navbar-nav">
                <li className="nav-item">
                  <LanguageSelector />
                </li>
              </div>
            </div>
          </>
        )}


      </div>
    </nav>
  );
};

export default Navbar; 